import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers3, Loader2, RefreshCw, Search, Trash2, Users, Wand2 } from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';

// Maça özel oran ekranı (Oyun Kontrol → Spor & Cashout → Özel Oranlar).
//
// Üç oran kaynağının fiyatı da salt okunurdur; bu ekran tek bir seçime
// yöneticinin oranını geçirir. Kayıt backend'de tek noktadan uygulanır
// (services/odds_override.py) ve **hem vitrinde hem kupon doğrulamasında**
// çalışır — yalnızca birine uygulansaydı oyuncunun gördüğü oranla oynadığı
// oran ayrışırdı.
//
// Olay listesi oyuncunun gördüğü vitrin ucundan okunur (`/sports/book/events`):
// panelde ayrı bir maç kataloğu tutulsaydı iki liste kaçınılmaz olarak
// ayrışırdı. Bu yüzden listede **yalnızca o an oynanabilir olaylar** görünür.

const FEED_LABELS = {
  sportsbook: 'Sportsbook API',
  oddsapi: 'The Odds API',
  apifootball: 'API-Football',
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const competitionId = (competition) => (
  competition ? `${competition.feed || 'sportsbook'}:${competition.short_name}` : ''
);

// Satırın hangi seçimi taşıdığı tek bir anahtarla eşleşir; backend de aynı
// dörtlüyü kullanır (sağlayıcı + olay + market + seçim).
const rowKey = (provider, matchId, marketKey, pickKey) => (
  `${provider}|${matchId}|${marketKey}|${pickKey}`
);

const SportsOddsOverrides = ({ competitions = [], onCountChange, onInspect }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [competition, setCompetition] = useState('');
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [marketBusy, setMarketBusy] = useState('');
  const [query, setQuery] = useState('');
  // Seçim başına girilen oran; kaydedilene kadar burada durur.
  const [drafts, setDrafts] = useState({});
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/sports/odds-overrides');
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { onCountChange?.(items.length); }, [items.length, onCountChange]);

  // İlk ligi kendiliğinden seçer; yönetici liste seçmeden boş ekran görmesin.
  useEffect(() => {
    if (!competition && competitions.length) {
      setCompetition(competitionId(competitions[0]));
    }
  }, [competitions, competition]);

  const loadEvents = useCallback(async (selected) => {
    if (!selected) { setEvents([]); return; }
    const [feed, shortName] = String(selected).split(':');
    setEventsLoading(true);
    try {
      const { data } = await client.get('/sports/book/events', {
        params: { competition: shortName, feed },
      });
      setEvents(data.events || []);
    } catch (error) {
      setEvents([]);
      toast({
        title: 'Maçlar yüklenemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setEventsLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadEvents(competition); }, [competition, loadEvents]);

  const loadAllMarkets = async (event) => {
    if (event.feed !== 'oddsapi') return;
    setMarketBusy(event.event_key);
    try {
      const { data } = await client.get('/sports/book/event-markets', {
        params: {
          event_id: event.event_key,
          sport_key: event.sport_key,
          feed: event.feed,
        },
      });
      setEvents((current) => current.map((item) => (
        item.event_key === event.event_key && item.feed === event.feed
          ? { ...item, markets: data.markets || item.markets, market_detail_loaded: true }
          : item
      )));
      toast({
        title: 'Tüm oranlar yüklendi',
        description: `${data.count || 0} pazar · ${data.selection_count || 0} oran`,
      });
    } catch (error) {
      toast({
        title: 'Ek oranlar alınamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setMarketBusy('');
    }
  };

  // Tanımlı oranlar seçim anahtarına göre indekslenir: maç listesinde hangi
  // butonun zaten özel oranlı olduğu görünmeli.
  const byKey = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      map[rowKey(item.provider, item.match_id, item.market_key || '', item.pick_key)] = item;
    });
    return map;
  }, [items]);

  const visibleEvents = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return events.slice(0, 40);
    return events
      .filter((event) => `${event.home} ${event.away} ${event.competition_name || ''}`
        .toLowerCase().includes(term))
      .slice(0, 40);
  }, [events, query]);

  const save = async (event, market, selection) => {
    const provider = event.feed || 'sportsbook';
    const key = rowKey(provider, event.event_key, market.market_key, selection.pick_key);
    const raw = drafts[key];
    const odd = Number(String(raw || '').replace(',', '.'));
    if (!odd || odd < 1.01 || odd > 1000) {
      toast({ title: 'Geçersiz oran', description: 'Oran 1.01 ile 1000 arasında olmalıdır.' });
      return;
    }
    setBusy(key);
    try {
      await client.post('/admin/sports/odds-overrides', {
        provider,
        match_id: event.event_key,
        market_key: market.market_key,
        pick_key: selection.pick_key,
        mode: 'fixed',
        odd,
        active: true,
        event_label: `${event.home} - ${event.away}`,
        market_label: market.label || '',
        selection_label: selection.label || '',
        sport_key: event.sport_key || '',
      });
      setDrafts((current) => ({ ...current, [key]: '' }));
      await load();
      // Vitrinin özel oranı göstermesi için olay listesi de tazelenir.
      loadEvents(competition);
      toast({
        title: 'Özel oran tanımlandı',
        description: `${event.home} - ${event.away} · ${selection.label} → ${odd.toFixed(2)}`,
      });
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  const remove = async (item) => {
    setBusy(item.id);
    try {
      await client.delete(`/admin/sports/odds-overrides/${item.id}`);
      await load();
      loadEvents(competition);
      toast({ title: 'Özel oran kaldırıldı', description: item.event_label || item.match_id });
    } catch (error) {
      toast({
        title: 'Kaldırılamadı',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  const toggleActive = async (item) => {
    setBusy(item.id);
    try {
      await client.post('/admin/sports/odds-overrides', {
        provider: item.provider,
        match_id: item.match_id,
        market_key: item.market_key || '',
        pick_key: item.pick_key,
        mode: item.mode || 'fixed',
        odd: item.odd,
        multiplier: item.multiplier,
        active: !item.active,
        event_label: item.event_label || '',
        market_label: item.market_label || '',
        selection_label: item.selection_label || '',
        sport_key: item.sport_key || '',
      });
      await load();
      loadEvents(competition);
    } catch (error) {
      toast({
        title: 'Güncellenemedi',
        description: error.response?.data?.detail || 'İstek başarısız',
      });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="gc-stack">
      <p className="text-[11px] text-slate-500">
        Tanımlanan oran hem oyuncunun gördüğü listede hem de kupon
        doğrulamasında geçerlidir. Listede yalnızca <strong>o an oynanabilir</strong>
        {' '}olaylar görünür; maç başladıktan sonra kayıt durur ama sağlayıcı
        oranı yayınlamayı bıraktığında seçim vitrinden düşer.
      </p>

      {/* --- Maç seçimi ------------------------------------------------- */}
      <div className="gc-inline">
        <select
          value={competition}
          onChange={(changeEvent) => setCompetition(changeEvent.target.value)}
          className="gc-input is-lg"
        >
          {competitions.length === 0 && <option value="">Açık oran kaynağı yok</option>}
          {competitions.map((item) => (
            <option key={competitionId(item)} value={competitionId(item)}>
              {item.name} · {FEED_LABELS[item.feed] || item.feed}
            </option>
          ))}
        </select>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={query}
            onChange={(changeEvent) => setQuery(changeEvent.target.value)}
            placeholder="Takım ara..."
            className="gc-input is-md gc-input-search"
          />
        </div>
        <button
          type="button"
          onClick={() => loadEvents(competition)}
          className="gc-btn is-sm"
        >
          <RefreshCw size={12} /> Yenile
        </button>
      </div>

      {eventsLoading ? (
        <div className="gc-empty is-row">
          <Loader2 size={13} className="gc-spin" /> Maçlar yükleniyor...
        </div>
      ) : visibleEvents.length === 0 ? (
        <div className="gc-empty">
          Seçili ligde oranlı maç bulunamadı.
        </div>
      ) : (
        <div className="gc-list">
          {visibleEvents.map((event) => (
            <div key={`${event.feed}:${event.event_key}`} className="gc-odds-event">
              <div className="gc-inline">
                <span className="text-sm font-medium">{event.home} - {event.away}</span>
                {event.live && (
                  <span className="gc-badge is-off">CANLI</span>
                )}
                <span className="text-[11px] text-slate-500 ml-auto">
                  {formatTime(event.start_time)} · {FEED_LABELS[event.feed] || event.feed}
                </span>
                {event.feed === 'oddsapi' && (
                  <button
                    type="button"
                    onClick={() => loadAllMarkets(event)}
                    disabled={marketBusy === event.event_key}
                    className="gc-btn is-xs"
                  >
                    {marketBusy === event.event_key
                      ? <Loader2 size={11} className="gc-spin" />
                      : <Layers3 size={11} />}
                    {event.market_detail_loaded ? `${event.markets?.length || 0} pazar` : 'Tüm oranlar'}
                  </button>
                )}
              </div>
              <div className="gc-list">
                {(event.markets || []).map((market) => (
                  <div key={market.market_key}>
                    <div className="text-[11px] text-slate-500 mb-1">{market.label}</div>
                    <div className="gc-btns">
                      {(market.selections || []).map((selection) => {
                        const provider = event.feed || 'sportsbook';
                        const key = rowKey(
                          provider, event.event_key, market.market_key, selection.pick_key);
                        const existing = byKey[key];
                        return (
                          <div
                            key={selection.pick_key}
                            className={`gc-odds-pick ${existing?.active ? 'is-active' : ''}`}
                          >
                            <span className="gc-odds-label">{selection.label}</span>
                            <span className={`gc-odds-price ${existing?.active ? 'is-custom' : ''}`}>
                              {Number(selection.odd).toFixed(2)}
                            </span>
                            <input
                              value={drafts[key] ?? ''}
                              onChange={(changeEvent) => setDrafts((current) => ({
                                ...current, [key]: changeEvent.target.value,
                              }))}
                              placeholder="özel"
                              inputMode="decimal"
                              className="gc-input is-xs gc-input-center"
                            />
                            <button
                              type="button"
                              onClick={() => save(event, market, selection)}
                              disabled={busy === key || !drafts[key]}
                              className="gc-btn is-xs is-primary"
                            >
                              {busy === key
                                ? <Loader2 size={11} className="gc-spin" />
                                : <Wand2 size={11} />}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- Tanımlı özel oranlar --------------------------------------- */}
      <div className="gc-subblock">
        <div className="gc-label">
          Tanımlı özel oranlar {loading ? '' : `(${items.length})`}
        </div>
        {loading ? (
          <div className="gc-empty is-row">
            <Loader2 size={13} className="gc-spin" /> Yükleniyor...
          </div>
        ) : items.length === 0 ? (
          <div className="gc-empty">Henüz özel oran tanımlanmadı.</div>
        ) : (
          <div className="gc-list">
            {items.map((item) => (
              <div key={item.id} className="gc-row">
                <div className="gc-row-main">
                  <b>{item.event_label || item.match_id}</b>
                  <small>
                    {[
                      FEED_LABELS[item.provider] || item.provider,
                      item.market_label,
                      item.selection_label || item.pick_key,
                    ].filter(Boolean).join(' · ')}
                    {item.admin_username ? ` · ${item.admin_username}` : ''}
                  </small>
                </div>
                <span className={`gc-odds-price ${item.active ? 'is-custom' : ''}`}>
                  {item.mode === 'multiplier'
                    ? `× ${Number(item.multiplier).toFixed(2)}`
                    : Number(item.odd).toFixed(2)}
                </span>
                {/* Özel oranın tek ölçülebilir sonucu onu kimin oynadığıdır;
                    binlerce üye içinden ayıklamanın başka yolu yok. İmza
                    backend'in kupona yazdığı anahtarla aynı biçimdedir
                    (services/odds_override.pick_signature). */}
                <button
                  type="button"
                  onClick={() => onInspect?.({
                    signature: rowKey(
                      item.provider, item.match_id, item.market_key || '',
                      item.pick_key),
                    label: [
                      item.event_label || item.match_id,
                      item.selection_label || item.pick_key,
                    ].filter(Boolean).join(' · '),
                  })}
                  className="gc-btn is-xs"
                  title="Spor Bahisleri sayfasını bu özel oranın süzgeciyle aç"
                >
                  <Users size={11} /> Kuponlar
                </button>
                <button
                  type="button"
                  onClick={() => toggleActive(item)}
                  disabled={busy === item.id}
                  className="gc-btn is-xs"
                >
                  {item.active ? 'Durdur' : 'Aktifleştir'}
                </button>
                <button
                  type="button"
                  onClick={() => remove(item)}
                  disabled={busy === item.id}
                  className="gc-btn is-xs is-danger"
                >
                  {busy === item.id
                    ? <Loader2 size={11} className="gc-spin" />
                    : <Trash2 size={11} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SportsOddsOverrides;
